import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  PHONE_COUNTRY_OPTIONS,
  defaultPhoneCountryFromE164,
  nationalDigitsFromE164,
  toFullE164,
} from '../utils/phoneCountries';
import { uploadsPublicUrl } from '../utils/uploadsPublicUrl';

const PRIMARY = '#00A7E3';
const ACCENT = '#102E5C';

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };

const Profile = () => {
  const { user, refreshUser, setUserDirect } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNational, setPhoneNational] = useState('');
  const [phoneCountryIso, setPhoneCountryIso] = useState('IN');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [country, setCountry] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setAddressLine1(user.shippingLine1 || '');
    setAddressLine2(user.shippingLine2 || '');
    setCity(user.shippingCity || '');
    setAddrState(user.shippingState || '');
    setCountry(user.shippingCountry || '');
    setZipcode(user.shippingZipcode || '');
    const p = user.phone || '';
    if (p) {
      const iso = defaultPhoneCountryFromE164(p);
      setPhoneCountryIso(iso);
      setPhoneNational(nationalDigitsFromE164(p, iso));
    } else {
      setPhoneCountryIso('IN');
      setPhoneNational('');
    }
  }, [user]);

  const avatarSrc = uploadsPublicUrl(user?.avatarUrl || '');

  const showMsg = useCallback((type, text) => {
    setMessage({ type, text });
    if (text) setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await api.post('/upload/account-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = await refreshUser();
      if (r.success) showMsg('success', 'Profile photo updated');
      else showMsg('error', 'Could not refresh your profile');
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (!String(firstName).trim() || !String(lastName).trim()) {
      showMsg('error', 'First and last name are required.');
      setSaving(false);
      return;
    }

    const national = phoneNational.replace(/\D/g, '');
    if (!national) {
      showMsg('error', 'Phone number is required.');
      setSaving(false);
      return;
    }
    if (national[0] === '0') {
      showMsg('error', 'Phone number should not start with 0.');
      setSaving(false);
      return;
    }
    const phonePayload = toFullE164(phoneCountryIso, national);
    if (!phonePayload) {
      showMsg('error', 'Enter a valid mobile number (8–15 digits total with the country you selected).');
      setSaving(false);
      return;
    }

    if (!String(addressLine1).trim() || !String(city).trim() || !String(addrState).trim() || !String(country).trim() || !String(zipcode).trim()) {
      showMsg('error', 'Fill in address line 1, city, state, country, and zip / postal code.');
      setSaving(false);
      return;
    }

    try {
      const { data } = await api.put('/auth/membership-shipping', {
        firstName: String(firstName).trim(),
        lastName: String(lastName).trim(),
        phone: phonePayload,
        addressLine1: String(addressLine1).trim(),
        addressLine2: String(addressLine2).trim(),
        city: String(city).trim(),
        state: String(addrState).trim(),
        country: String(country).trim(),
        zipcode: String(zipcode).trim(),
      });
      setUserDirect(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      showMsg('success', 'Contact and shipping details saved');
    } catch (err) {
      const d = err.response?.data;
      const msg =
        d?.message ||
        (Array.isArray(d?.errors) && d.errors[0]?.msg) ||
        'Could not save profile';
      showMsg('error', msg);
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT, mb: 3 }}>
        Profile
      </Typography>

      {message.text && (
        <Alert severity={message.type === 'success' ? 'success' : 'error'} sx={{ mb: 2, borderRadius: 1 }}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT, mb: 2 }}>
          Account
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 4, md: 6 }} columnSpacing={{ sm: 5, md: 8 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Email
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{user.email}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Plan
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
              {user.plan || 'free'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT, mb: 2 }}>
          Profile photo
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Avatar
            src={avatarSrc || undefined}
            sx={{ width: 96, height: 96, bgcolor: PRIMARY, fontSize: '2rem', fontWeight: 700 }}
          >
            {(user.firstName || 'U').charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="account-avatar-upload"
              type="file"
              onChange={handleAvatarChange}
              disabled={uploading}
            />
            <label htmlFor="account-avatar-upload">
              <Button
                variant="contained"
                component="span"
                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                disabled={uploading}
                sx={{ borderRadius: 1, textTransform: 'none', bgcolor: PRIMARY, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}
              >
                {uploading ? 'Uploading…' : 'Change photo'}
              </Button>
            </label>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              JPG or PNG, up to 5 MB. Square images look best.
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} component="form" onSubmit={handleSave} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ACCENT, mb: 2 }}>
          Contact & shipping
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              size="small"
              autoComplete="given-name"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              size="small"
              autoComplete="family-name"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Phone number
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 }, ...fieldSx }}>
                <InputLabel id="profile-country-label">Country code</InputLabel>
                <Select
                  labelId="profile-country-label"
                  label="Country code"
                  value={phoneCountryIso}
                  onChange={(e) => setPhoneCountryIso(e.target.value)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  {PHONE_COUNTRY_OPTIONS.map((o) => (
                    <MenuItem key={o.code} value={o.code} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {o.label} {o.dialDisplay}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Mobile number"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                placeholder="Your mobile number"
                value={phoneNational}
                onChange={(e) => setPhoneNational(e.target.value)}
                size="small"
                sx={fieldSx}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address line 1"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              required
              size="small"
              autoComplete="address-line1"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address line 2 (optional)"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              size="small"
              autoComplete="address-line2"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
              size="small"
              autoComplete="address-level2"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="State / Province"
              value={addrState}
              onChange={(e) => setAddrState(e.target.value)}
              required
              size="small"
              autoComplete="address-level1"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              required
              size="small"
              autoComplete="country-name"
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Zip / Postal code"
              value={zipcode}
              onChange={(e) => setZipcode(e.target.value)}
              required
              size="small"
              autoComplete="postal-code"
              sx={fieldSx}
            />
          </Grid>
        </Grid>

        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={{
            mt: 3,
            px: 3,
            py: 1,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: PRIMARY,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' },
          }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Save changes'}
        </Button>
      </Paper>
    </Box>
  );
};

export default Profile;
